/**
 * Unified File Upload Component
 * 
 * Features:
 * - Drag and drop interface
 * - Real-time upload progress for each file
 * - Direct S3 upload with resilient error handling
 * - Bulk upload support with sequential processing
 * - Visual feedback and status tracking
 */

import React, { useState, useRef } from 'react';
import { Card, Badge, ProgressBar, Alert } from 'react-bootstrap';
import { CloudUpload, FileEarmark, CheckCircleFill, XCircleFill, Clock } from 'react-bootstrap-icons';
import { getBulkUploadUrls } from '../api/cases';
import { trackFileUpload, trackUploadError, trackEvent, captureError } from '../utils/analytics';

export interface FileUploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface FileUploadZoneProps {
  case_id: string;
  id_token?: string;
  onUploadComplete?: () => void;
  compact?: boolean; // For attorney view's inline style
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ 
  case_id, 
  id_token,
  onUploadComplete,
  compact = false 
}) => {
  const [uploadQueue, setUploadQueue] = useState<FileUploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFilesSelected(droppedFiles);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    handleFilesSelected(selectedFiles);
    // Reset input so same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFilesSelected = async (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;

    trackEvent('file_upload_started', { file_count: selectedFiles.length, case_id });

    // Get current queue length before adding new files
    const startIndex = uploadQueue.length;
    
    // Add files to upload queue
    const newUploads: FileUploadProgress[] = selectedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }));
    
    setUploadQueue(prev => [...prev, ...newUploads]);
    
    // Upload files with concurrency limit (3 at a time)
    const concurrencyLimit = 3;
    const uploadPromises: Promise<void>[] = [];
    
    for (let i = 0; i < selectedFiles.length; i += concurrencyLimit) {
      const batch = selectedFiles.slice(i, i + concurrencyLimit);
      const batchPromises = batch.map((file, batchIndex) => {
        const queueIndex = startIndex + i + batchIndex;
        const isLastFile = (i + batchIndex) === selectedFiles.length - 1;
        return uploadFile(file, queueIndex, isLastFile);
      });
      
      // Wait for current batch to complete before starting next batch
      await Promise.all(batchPromises);
    }
  };

  const uploadFile = async (file: File, queueIndex: number, isLastFile: boolean) => {
    try {
      if (!id_token) {
        throw new Error('Authentication required');
      }

      // Get upload URL from backend (only once - creates DB entry)
      const response = await getBulkUploadUrls(
        case_id,
        [{ 
          filename: file.name, 
          fileCategory: '', // Empty = auto-detect
          fileType: file.type || 'application/octet-stream' 
        }],
        id_token
      );

      const uploadInfo = response.data.uploads?.[0];
      if (!uploadInfo || !uploadInfo.uploadUrl) {
        throw new Error(uploadInfo?.error || 'Failed to get upload URL');
      }
      
      const { uploadUrl } = uploadInfo;
      
      // Attempt upload with retries (reuses same URL - no new DB entries)
      await attemptUpload(file, uploadUrl, queueIndex, 0);
      
      // Update status to success
      setUploadQueue(prev => prev.map((item, idx) => 
        idx === queueIndex ? { ...item, status: 'success', progress: 100 } : item
      ));

      trackFileUpload(1, case_id);
      
      // If this is the last file, trigger refresh after a brief delay
      if (isLastFile && onUploadComplete) {
        setTimeout(() => {
          onUploadComplete();
        }, 1500);
      }
      
    } catch (err: any) {
      console.error('Upload error:', err);
      // Error already handled in attemptUpload or URL generation
      setUploadQueue(prev => prev.map((item, idx) => 
        idx === queueIndex ? { 
          ...item, 
          status: 'error', 
          error: err.message 
        } : item
      ));
      trackUploadError(case_id, err.message);
      captureError(err instanceof Error ? err : new Error(err.message), { case_id, queueIndex });
    }
  };

  const attemptUpload = async (file: File, uploadUrl: string, queueIndex: number, retryCount: number): Promise<void> => {
    const maxRetries = 3;
    
    try {
      // Update status to uploading
      setUploadQueue(prev => prev.map((item, idx) => 
        idx === queueIndex ? { ...item, status: 'uploading' } : item
      ));
      
      // Upload to S3 with progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadQueue(prev => prev.map((item, idx) => 
              idx === queueIndex ? { ...item, progress } : item
            ));
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
        
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });
      
    } catch (err: any) {
      console.error(`Upload attempt ${retryCount + 1}/${maxRetries + 1} failed:`, err);
      
      // Retry on network errors with exponential backoff
      const isNetworkError = err.message.includes('Network error') || 
                            err.message.includes('Upload failed') ||
                            err.message.includes('Upload aborted');
      
      if (isNetworkError && retryCount < maxRetries) {
        const delayMs = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Retrying upload in ${delayMs}ms...`);
        
        // Update UI to show retry
        setUploadQueue(prev => prev.map((item, idx) => 
          idx === queueIndex ? { 
            ...item, 
            status: 'pending',
            progress: 0,
            error: `Retrying (${retryCount + 1}/${maxRetries})...` 
          } : item
        ));
        
        await new Promise(resolve => setTimeout(resolve, delayMs));
        // Retry with same uploadUrl (no new DB entry)
        return attemptUpload(file, uploadUrl, queueIndex, retryCount + 1);
      }
      
      // Max retries exceeded or non-network error
      const errorMessage = retryCount >= maxRetries 
        ? `Failed after ${maxRetries} retries: ${err.message}` 
        : err.message;
      
      throw new Error(errorMessage);
    }
  };

  const clearCompleted = () => {
    setUploadQueue(prev => prev.filter(item => item.status !== 'success'));
  };

  const clearAll = () => {
    setUploadQueue([]);
  };

  const hasUploads = uploadQueue.length > 0;
  const hasCompleted = uploadQueue.some(item => item.status === 'success');
  const hasErrors = uploadQueue.some(item => item.status === 'error');

  // Consolidated view kicks in for large batches
  const useConsolidated = uploadQueue.length > 10;
  const doneCount = uploadQueue.filter(i => i.status === 'success').length;
  const errorCount = uploadQueue.filter(i => i.status === 'error').length;
  const pendingCount = uploadQueue.filter(i => i.status === 'pending').length;
  const currentlyUploading = uploadQueue.find(i => i.status === 'uploading');
  const allDone = hasUploads && uploadQueue.every(i => i.status === 'success' || i.status === 'error');
  const overallPct = uploadQueue.length === 0 ? 0
    : Math.round(
        uploadQueue.reduce((sum, i) => {
          if (i.status === 'success') return sum + 100;
          if (i.status === 'uploading') return sum + i.progress;
          return sum;
        }, 0) / uploadQueue.length
      );

  return (
    <div>
      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: isDragging ? '3px dashed #0d6efd' : '2px dashed #dee2e6',
          borderRadius: '12px',
          padding: compact ? '30px 20px' : '40px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: isDragging ? '#e7f3ff' : '#f8f9fa',
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          id="file-input"
        />
        <CloudUpload size={compact ? 40 : 48} className={isDragging ? 'text-primary' : 'text-muted'} />
        <p className={`mt-3 mb-1 ${compact ? '' : 'fw-semibold'}`}>
          {isDragging ? 'Drop files here' : 'Drag and drop files here'}
        </p>
        <p className="text-muted small mb-0">or click to browse</p>
      </div>

      {/* Upload Queue */}
      {hasUploads && (
        <Card className="mt-3 border-0 shadow-sm">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
            <div className="d-flex align-items-center gap-2">
              <strong>Uploading</strong>
              <Badge bg="secondary">{uploadQueue.length} files</Badge>
              {allDone && errorCount === 0 && <Badge bg="success">All done</Badge>}
              {allDone && errorCount > 0 && <Badge bg="warning">{errorCount} failed</Badge>}
            </div>
            <div className="d-flex gap-2">
              {hasCompleted && (
                <button className="btn btn-sm btn-outline-secondary" onClick={clearCompleted}>
                  Clear Completed
                </button>
              )}
              <button className="btn btn-sm btn-outline-danger" onClick={clearAll}>
                Clear All
              </button>
            </div>
          </Card.Header>

          <Card.Body className={useConsolidated ? 'py-3 px-4' : 'p-0'}>
            {useConsolidated ? (
              /* ── Consolidated view for large batches ── */
              <div>
                {/* Overall progress bar */}
                <ProgressBar
                  now={overallPct}
                  variant={allDone && errorCount > 0 ? 'warning' : allDone ? 'success' : 'primary'}
                  style={{ height: '12px', borderRadius: '6px' }}
                  animated={!allDone}
                  striped={!allDone}
                />

                {/* Counts row */}
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                    {overallPct}% — {doneCount} of {uploadQueue.length} uploaded
                  </span>
                  <div className="d-flex gap-3" style={{ fontSize: '0.8rem' }}>
                    {doneCount > 0 && (
                      <span className="text-success d-flex align-items-center gap-1">
                        <CheckCircleFill size={12} /> {doneCount} done
                      </span>
                    )}
                    {errorCount > 0 && (
                      <span className="text-danger d-flex align-items-center gap-1">
                        <XCircleFill size={12} /> {errorCount} failed
                      </span>
                    )}
                    {pendingCount > 0 && (
                      <span className="text-secondary d-flex align-items-center gap-1">
                        <Clock size={12} /> {pendingCount} waiting
                      </span>
                    )}
                  </div>
                </div>

                {/* Current file status */}
                <div
                  className="mt-2 text-muted d-flex align-items-center gap-2"
                  style={{ fontSize: '0.82rem', minHeight: '1.4rem' }}
                >
                  {currentlyUploading ? (
                    <>
                      <FileEarmark size={13} className="flex-shrink-0" />
                      <span className="text-truncate">
                        Uploading <strong>{currentlyUploading.file.name}</strong>
                        {' '}({currentlyUploading.progress}%)
                      </span>
                    </>
                  ) : allDone ? (
                    errorCount === 0
                      ? <span className="text-success">All files uploaded successfully.</span>
                      : <span>Upload complete — {errorCount} file{errorCount > 1 ? 's' : ''} failed.</span>
                  ) : (
                    <span>Preparing next file…</span>
                  )}
                </div>

                {/* Collapsible error list */}
                {errorCount > 0 && (
                  <div className="mt-2 border-top pt-2">
                    <p className="text-danger small mb-1 fw-semibold">Failed files:</p>
                    {uploadQueue
                      .filter(i => i.status === 'error')
                      .map((i, idx) => (
                        <div key={idx} className="d-flex align-items-start gap-1 small text-danger">
                          <XCircleFill size={12} className="flex-shrink-0 mt-1" />
                          <span>{i.file.name} — {i.error}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              /* ── Per-file list for small batches ── */
              <div className="list-group list-group-flush">
                {uploadQueue.map((upload, idx) => (
                  <div key={idx} className="list-group-item py-2">
                    <div className="d-flex align-items-center gap-2">
                      <FileEarmark className="text-muted flex-shrink-0" size={16} />
                      <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                        <span className="fw-semibold">{upload.file.name}</span>
                        <span className="text-muted ms-2 small">
                          ({(upload.file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      {upload.status === 'uploading' && (
                        <div style={{ width: '100px' }}>
                          <ProgressBar now={upload.progress} variant="primary" style={{ height: '4px' }} />
                          <small className="text-muted">{upload.progress}%</small>
                        </div>
                      )}
                      <div className="flex-shrink-0">
                        {upload.status === 'success' && (
                          <Badge bg="success" className="d-flex align-items-center">
                            <CheckCircleFill size={12} className="me-1" /> Done
                          </Badge>
                        )}
                        {upload.status === 'error' && (
                          <Badge bg="danger" className="d-flex align-items-center" title={upload.error}>
                            <XCircleFill size={12} className="me-1" /> Failed
                          </Badge>
                        )}
                        {upload.status === 'pending' && (
                          <Badge bg="secondary" className="d-flex align-items-center">
                            <Clock size={12} className="me-1" /> {upload.error || 'Waiting'}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {upload.status === 'error' && upload.error && (
                      <div className="mt-1 ms-4">
                        <small className="text-danger">{upload.error}</small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {hasErrors && !useConsolidated && (
        <Alert variant="warning" className="mt-3 mb-0">
          <small>
            Some files failed to upload. Please try again or contact support if the problem persists.
          </small>
        </Alert>
      )}
    </div>
  );
};

export default FileUploadZone;
